import { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from "react";
import classNames from "classnames";

export type ButtonVariant = "primary" | "quiet" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

/*
 * Every control that isn't a field is one of these four. The variants exist so that
 * "which button is the one that acts" is decided once here rather than at each call
 * site, which is how the old sidebar ended up with Add, Log in and Delete all drawn
 * identically.
 */
const base =
  "inline-flex items-center justify-center gap-1.5 rounded-md font-medium select-none " +
  "whitespace-nowrap transition-colors cursor-pointer " +
  "disabled:opacity-45 disabled:pointer-events-none " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";

const variants: Record<ButtonVariant, string> = {
  // A tint rather than a slab of accent. Solid, it was the loudest thing on the screen
  // by some margin, which is a lot of emphasis for a control that is pressed rarely and
  // sits above a list meant to be read.
  primary: "bg-accent/10 text-accent hover:bg-accent/20",
  quiet:
    "border border-line-strong bg-surface-raised text-ink hover:bg-surface-sunken",
  ghost: "text-ink-muted hover:text-ink hover:bg-surface-sunken",
  danger: "border border-line-strong text-danger hover:bg-danger/10",
};

/*
 * Both sizes clear 32px of height on touch. `sm` is the sidebar's furniture, `md` the
 * things a modal asks you to press.
 */
const sizes: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-sm",
};

export function buttonClass(
  variant: ButtonVariant = "quiet",
  size: ButtonSize = "sm",
  className?: string
) {
  return classNames(base, variants[variant], sizes[size], className);
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
};

export function Button({
  variant,
  size,
  className,
  type = "button",
  children,
  ...rest
}: ButtonProps) {
  return (
    <button type={type} className={buttonClass(variant, size, className)} {...rest}>
      {children}
    </button>
  );
}

export type LinkButtonProps = AnchorHTMLAttributes<HTMLAnchorElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
};

/** The same shapes, for the places the control is genuinely a link out. */
export function LinkButton({
  variant,
  size,
  className,
  children,
  ...rest
}: LinkButtonProps) {
  return (
    <a className={buttonClass(variant, size, className)} {...rest}>
      {children}
    </a>
  );
}

/**
 * A square button holding nothing but an icon.
 *
 * Takes `label` rather than `aria-label` so it cannot be left off: there is no text
 * inside to fall back on, and an unlabelled one is announced as "button".
 */
/*
 * A size prop rather than a `size-*` in `className`.
 *
 * Tailwind resolves competing utilities by their order in the stylesheet, not their
 * order in the attribute — so a `size-6` passed in alongside a baked-in `size-8` lost,
 * silently, and the button came out 32px. That made the row holding it 8px taller than
 * its neighbours, and the list jumped every time the selection moved.
 */
const iconSizes: Record<ButtonSize, string> = {
  sm: "size-6",
  md: "size-8",
};

export function IconButton({
  label,
  variant = "ghost",
  size = "md",
  className,
  type = "button",
  children,
  ...rest
}: Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> & {
  label: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  children: ReactNode;
}) {
  return (
    <button
      type={type}
      aria-label={label}
      title={label}
      className={classNames(
        base,
        variants[variant],
        iconSizes[size],
        "shrink-0 p-0 text-base",
        className
      )}
      {...rest}
    >
      {children}
    </button>
  );
}
